import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Trophy, Users } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface TeamLeaderboardProps {
  challengeId: string;
}

export function TeamLeaderboard({ challengeId }: TeamLeaderboardProps) {
  const { data: teams } = useQuery({
    queryKey: ['challenge-teams', challengeId],
    queryFn: async () => {
      const { data: teamsData, error: teamsError } = await supabase
        .from('challenge_teams')
        .select('*')
        .eq('challenge_id', challengeId);

      if (teamsError) throw teamsError;
      if (!teamsData || teamsData.length === 0) return [];

      // Get participants for each team
      const teamStats = await Promise.all(
        teamsData.map(async (team) => {
          const { data: participants } = await supabase
            .from('challenge_participants')
            .select('progress_data')
            .eq('challenge_id', challengeId)
            .eq('team_id', team.id);

          const totalCompleted = participants?.reduce((sum, p) => {
            const progress = p.progress_data as any;
            return sum + (progress?.completed_days || 0);
          }, 0) || 0;

          const totalPossible = (participants?.length || 0) * 30; // Assuming 30 day average
          const successRate = totalPossible > 0 ? Math.round((totalCompleted / totalPossible) * 100) : 0;

          return {
            ...team,
            member_count: participants?.length || 0,
            total_completed: totalCompleted,
            success_rate: successRate,
          };
        })
      );

      return teamStats.sort((a, b) => b.success_rate - a.success_rate);
    },
  });

  if (!teams || teams.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-primary" />
          Team Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {teams.map((team, index) => (
          <div
            key={team.id}
            className="p-4 rounded-lg border bg-card"
            style={{
              borderLeftWidth: '4px',
              borderLeftColor: team.team_color || 'hsl(182,37%,46%)',
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white"
                  style={{ backgroundColor: team.team_color || 'hsl(182,37%,46%)' }}
                >
                  {index + 1}
                </div>
                <div>
                  <p className="font-semibold">{team.team_name}</p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Users className="h-3 w-3" />
                    <span>{team.member_count} members</span>
                  </div>
                </div>
              </div>
              <Badge variant={index === 0 ? "default" : "secondary"}>
                {team.success_rate}%
              </Badge>
            </div>
            <Progress value={team.success_rate} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {team.total_completed} days completed
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
